/**
 * Deployment regions. Each region pins the settlement currency and the
 * regulatory surface the platform must present: e-invoicing clearance,
 * the payment rail used for disbursement, and the basis for strong
 * authentication on payer confirmation.
 */

export const REGION_CODES = ['GLOBAL', 'UAE', 'KEN', 'NGA', 'UGA', 'EU'] as const;
export type RegionCode = (typeof REGION_CODES)[number];

export interface RegionConfig {
  code: RegionCode;
  name: string;
  currency: string;
  /** E-invoicing regime whose clearance reference we store against a deal */
  eInvoicing: string;
  /** Field label for the clearance reference in the UI */
  eInvoiceRefLabel: string;
  /** Instant payment rail used for disbursement and settlement */
  rail: string;
  /** Strong-authentication basis for payer confirmation */
  auth: string;
  /** AML / sanctions screening basis */
  aml: string;
  /** Business days offset applied when projecting settlement dates */
  settlementLagDays: number;
}

export const REGIONS: Record<RegionCode, RegionConfig> = {
  GLOBAL: {
    code: 'GLOBAL',
    name: 'Global / Multi-market',
    currency: 'USD',
    eInvoicing: 'Generic e-invoice clearance',
    eInvoiceRefLabel: 'E-invoice clearance ref',
    rail: 'Local instant rail',
    auth: 'OTP-gated secure action link',
    aml: 'AML & sanctions screening',
    settlementLagDays: 1,
  },
  UAE: {
    code: 'UAE',
    name: 'United Arab Emirates',
    currency: 'AED',
    eInvoicing: 'CBUAE e-invoicing (PINT AE)',
    eInvoiceRefLabel: 'PINT AE clearance ref',
    rail: 'Jaywan / Aani',
    auth: 'OTP per Notice 2025/3057',
    aml: 'AML per Federal Decree-Law 6/2025',
    settlementLagDays: 0,
  },
  KEN: {
    code: 'KEN',
    name: 'Kenya',
    currency: 'KES',
    eInvoicing: 'KRA eTIMS',
    eInvoiceRefLabel: 'eTIMS control unit invoice number',
    rail: 'PesaLink / M-Pesa',
    auth: 'OTP-gated approval',
    aml: 'AML & sanctions screening',
    settlementLagDays: 0,
  },
  NGA: {
    code: 'NGA',
    name: 'Nigeria',
    currency: 'NGN',
    eInvoicing: 'FIRS e-invoice',
    eInvoiceRefLabel: 'FIRS IRN',
    rail: 'NIBSS Instant (NIP)',
    auth: 'OTP-gated approval',
    aml: 'AML & sanctions screening',
    settlementLagDays: 0,
  },
  UGA: {
    code: 'UGA',
    name: 'Uganda',
    currency: 'UGX',
    eInvoicing: 'URA EFRIS',
    eInvoiceRefLabel: 'EFRIS FDN',
    rail: 'MTN MoMo / Airtel Money',
    auth: 'OTP-gated approval',
    aml: 'AML & sanctions screening',
    settlementLagDays: 1,
  },
  EU: {
    code: 'EU',
    name: 'European Union',
    currency: 'EUR',
    eInvoicing: 'Peppol (ViDA)',
    eInvoiceRefLabel: 'Peppol document ID',
    rail: 'SEPA Instant',
    auth: 'Strong customer authentication (PSD2)',
    aml: 'AMLD screening',
    settlementLagDays: 0,
  },
};

export function region(code: string): RegionConfig {
  const r = REGIONS[code as RegionCode];
  if (!r) throw new Error(`Unknown region: ${code}`);
  return r;
}
